'use client';
import React, { useState } from 'react';
import { Building2, Globe, Download, Upload, CheckCircle2, Plus, Database, BookOpen, Award, Zap, Settings, Code2, RefreshCw, ExternalLink } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import Icon from '@/components/ui/AppIcon';


type PartnerStatus = 'active' | 'pending' | 'inactive';
type PartnerType = 'university' | 'school_district' | 'company' | 'research_lab';

interface Partner {
  id: string;
  name: string;
  type: PartnerType;
  location: string;
  status: PartnerStatus;
  coursesShared: number;
  studentsReached: number;
  integrationMethod: string;
  joinedDate: string;
  accreditation?: string;
}

interface APIEndpoint {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  description: string;
  status: 'stable' | 'beta' | 'deprecated';
}

const partners: Partner[] = [
  { id: 'p-001', name: 'Massachusetts Institute of Technology', type: 'university', location: 'Cambridge, MA', status: 'active', coursesShared: 12, studentsReached: 4200, integrationMethod: 'API + LMS Bridge', joinedDate: '2025-09', accreditation: 'NECHE Accredited' },
  { id: 'p-002', name: 'Chicago Public Schools', type: 'school_district', location: 'Chicago, IL', status: 'active', coursesShared: 8, studentsReached: 18500, integrationMethod: 'SCORM Export', joinedDate: '2025-11', accreditation: 'State Certified' },
  { id: 'p-003', name: 'DeepMind Research', type: 'research_lab', location: 'London, UK', status: 'pending', coursesShared: 0, studentsReached: 0, integrationMethod: 'API Integration', joinedDate: '2026-07' },
  { id: 'p-004', name: 'SpaceX Education Initiative', type: 'company', location: 'Hawthorne, CA', status: 'active', coursesShared: 3, studentsReached: 890, integrationMethod: 'Direct API', joinedDate: '2026-01' },
  { id: 'p-005', name: 'Houston Independent School District', type: 'school_district', location: 'Houston, TX', status: 'inactive', coursesShared: 5, studentsReached: 0, integrationMethod: 'LTI 1.3', joinedDate: '2025-08' },
];

const apiEndpoints: APIEndpoint[] = [
  { method: 'GET', path: '/api/v1/curriculum/catalog', description: 'Retrieve full course catalog with metadata', status: 'stable' },
  { method: 'POST', path: '/api/v1/curriculum/ingest', description: 'Ingest external curriculum packages (SCORM, xAPI)', status: 'stable' },
  { method: 'GET', path: '/api/v1/curriculum/{id}/export', description: 'Export a course in LMS-compatible format', status: 'stable' },
  { method: 'POST', path: '/api/v1/students/sync', description: 'Sync student enrollment and progress data', status: 'beta' },
  { method: 'GET', path: '/api/v1/accreditation/pathways', description: 'List available certification and accreditation pathways', status: 'stable' },
  { method: 'POST', path: '/api/v1/partnerships/register', description: 'Register a new institutional partnership', status: 'stable' },
  { method: 'PUT', path: '/api/v1/curriculum/{id}/update', description: 'Push curriculum updates to partner LMS systems', status: 'beta' },
];

const typeConfig: Record<PartnerType, { label: string; icon: React.ElementType; color: string }> = {
  university: { label: 'University', icon: Building2, color: 'text-blue-600 bg-blue-50' },
  school_district: { label: 'School District', icon: BookOpen, color: 'text-emerald-600 bg-emerald-50' },
  company: { label: 'Company', icon: Zap, color: 'text-amber-600 bg-amber-50' },
  research_lab: { label: 'Research Lab', icon: Database, color: 'text-purple-600 bg-purple-50' },
};

const statusConfig: Record<PartnerStatus, { label: string; color: string }> = {
  active: { label: 'Active', color: 'bg-green-50 text-green-700 border-green-200' },
  pending: { label: 'Pending', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  inactive: { label: 'Inactive', color: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const methodColor: Record<string, string> = {
  GET: 'bg-blue-50 text-blue-700 border-blue-200',
  POST: 'bg-green-50 text-green-700 border-green-200',
  PUT: 'bg-amber-50 text-amber-700 border-amber-200',
  DELETE: 'bg-red-50 text-red-700 border-red-200',
};

export default function InstitutionalIntegrationContent() {
  const [activeTab, setActiveTab] = useState<'partners' | 'api' | 'lms' | 'accreditation'>('partners');
  const [addPartnerOpen, setAddPartnerOpen] = useState(false);
  const [selectedEndpoint, setSelectedEndpoint] = useState<APIEndpoint | null>(null);
  const [endpointModalOpen, setEndpointModalOpen] = useState(false);

  const stats = [
    { label: 'Active Partners', value: partners.filter(p => p.status === 'active').length, icon: Building2, color: 'text-blue-600 bg-blue-50' },
    { label: 'Students Reached', value: partners.reduce((a, p) => a + p.studentsReached, 0).toLocaleString(), icon: Globe, color: 'text-green-600 bg-green-50' },
    { label: 'Courses Shared', value: partners.reduce((a, p) => a + p.coursesShared, 0), icon: BookOpen, color: 'text-purple-600 bg-purple-50' },
    { label: 'API Endpoints', value: apiEndpoints.length, icon: Code2, color: 'text-amber-600 bg-amber-50' },
  ];

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-foreground">Institutional Integration Layer</h1>
          <p className="text-sm text-muted-foreground mt-1">University partnerships, LMS import/export, API endpoints, and accreditation pathways</p>
        </div>
        <button
          onClick={() => setAddPartnerOpen(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold btn-scale hover:bg-primary/90 transition-colors"
        >
          <Plus size={16} /> Add Partner
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-card border border-border rounded-2xl p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${s.color}`}>
                <Icon size={18} />
              </div>
              <div>
                <p className="text-xl font-extrabold text-foreground font-tabular">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border overflow-x-auto">
        {(['partners', 'api', 'lms', 'accreditation'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap transition-all border-b-2 -mb-px ${
              activeTab === tab ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab === 'partners' ? 'Partners' : tab === 'api' ? 'API Endpoints' : tab === 'lms' ? 'LMS Import/Export' : 'Accreditation'}
          </button>
        ))}
      </div>

      {/* Partners Tab */}
      {activeTab === 'partners' && (
        <div className="space-y-3">
          {partners.map((p) => {
            const TypeIcon = typeConfig[p.type].icon;
            return (
              <div key={p.id} className="bg-card border border-border rounded-2xl p-5 card-hover">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${typeConfig[p.type].color}`}>
                    <TypeIcon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-semibold text-muted-foreground">{typeConfig[p.type].label}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${statusConfig[p.status].color}`}>
                        {statusConfig[p.status].label}
                      </span>
                      {p.accreditation && (
                        <span className="flex items-center gap-0.5 text-xs text-purple-700 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded-full">
                          <Award size={9} /> {p.accreditation}
                        </span>
                      )}
                    </div>
                    <h3 className="text-sm font-bold text-foreground">{p.name}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{p.location} · Joined {p.joinedDate} · {p.integrationMethod}</p>
                    {p.status === 'active' && (
                      <div className="flex items-center gap-4 mt-2">
                        <span className="text-xs text-muted-foreground"><span className="font-semibold text-foreground">{p.coursesShared}</span> courses shared</span>
                        <span className="text-xs text-muted-foreground"><span className="font-semibold text-foreground">{p.studentsReached.toLocaleString()}</span> students reached</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button className="flex items-center gap-1.5 px-3 py-2 bg-muted hover:bg-muted/80 rounded-lg text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors">
                      <Settings size={12} /> Manage
                    </button>
                    {p.status === 'active' && (
                      <button className="flex items-center gap-1.5 px-3 py-2 bg-primary/10 text-primary rounded-lg text-xs font-semibold hover:bg-primary/20 transition-colors">
                        <RefreshCw size={12} /> Sync
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* API Endpoints Tab */}
      {activeTab === 'api' && (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-base font-bold text-foreground">Curriculum Ingestion API</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Base URL: <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">https://api.jarvondisu.edu/v1</code></p>
              </div>
              <span className="text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-1 rounded-lg font-semibold">API v1 · Stable</span>
            </div>
            <div className="space-y-2">
              {apiEndpoints.map((ep) => (
                <div
                  key={ep.path}
                  className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-muted/40 rounded-xl hover:bg-muted/70 transition-colors cursor-pointer"
                  onClick={() => { setSelectedEndpoint(ep); setEndpointModalOpen(true); }}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className={`text-xs font-bold px-2 py-1 rounded-lg border font-mono shrink-0 ${methodColor[ep.method]}`}>{ep.method}</span>
                    <code className="text-xs font-mono text-foreground truncate">{ep.path}</code>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-muted-foreground hidden sm:block">{ep.description}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${ep.status === 'stable' ? 'bg-green-50 text-green-700' : ep.status === 'beta' ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
                      {ep.status}
                    </span>
                    <ExternalLink size={12} className="text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* LMS Import/Export Tab */}
      {activeTab === 'lms' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-card border border-border rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
                <Download size={16} className="text-blue-600" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-foreground">Import Curriculum</h3>
                <p className="text-xs text-muted-foreground">Ingest from external LMS</p>
              </div>
            </div>
            <div className="space-y-2 mb-4">
              {['SCORM 1.2 / 2004', 'xAPI (Tin Can)', 'IMS Common Cartridge', 'LTI 1.3', 'QTI Quiz Format'].map((fmt) => (
                <div key={fmt} className="flex items-center gap-2 text-sm text-foreground">
                  <CheckCircle2 size={13} className="text-primary shrink-0" /> {fmt}
                </div>
              ))}
            </div>
            <div className="border-2 border-dashed border-border rounded-xl p-4 text-center hover:border-primary/40 transition-colors cursor-pointer">
              <Upload size={20} className="mx-auto text-muted-foreground mb-1.5" />
              <p className="text-xs font-semibold text-foreground">Drop LMS package here</p>
              <p className="text-xs text-muted-foreground mt-0.5">or browse to upload</p>
            </div>
          </div>

          <div className="bg-card border border-border rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
                <Download size={16} className="text-emerald-600" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-foreground">Export Curriculum</h3>
                <p className="text-xs text-muted-foreground">Send to partner LMS</p>
              </div>
            </div>
            <div className="space-y-2 mb-4">
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 block">Select Course</label>
                <select className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30">
                  <option>AI Development (v3.2.0)</option>
                  <option>Intro to Coding (v4.0.0)</option>
                  <option>Aerospace Systems (v1.3.0)</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 block">Export Format</label>
                <select className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30">
                  <option>SCORM 2004</option><option>xAPI Package</option><option>IMS Common Cartridge</option><option>PDF Bundle</option>
                </select>
              </div>
            </div>
            <button className="w-full py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold btn-scale hover:bg-primary/90 transition-colors">
              <Download size={14} className="inline mr-1.5" /> Export Package
            </button>
          </div>

          <div className="md:col-span-2 bg-card border border-border rounded-2xl p-5">
            <h3 className="text-sm font-bold text-foreground mb-3">Compatible LMS Platforms</h3>
            <div className="flex flex-wrap gap-2">
              {['Canvas', 'Blackboard', 'Moodle', 'Google Classroom', 'Schoology', 'D2L Brightspace', 'Instructure', 'Sakai', 'Open edX'].map((lms) => (
                <span key={lms} className="text-xs bg-muted text-muted-foreground px-3 py-1.5 rounded-lg font-semibold border border-border">
                  {lms}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Accreditation Tab */}
      {activeTab === 'accreditation' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { title: 'K-12 State Certification', body: 'State Education Boards', courses: 8, status: 'active', icon: Award, color: 'text-blue-600 bg-blue-50' },
              { title: 'Higher Education Credit', body: 'Regional Accreditors (NECHE, HLC)', courses: 5, status: 'active', icon: Building2, color: 'text-purple-600 bg-purple-50' },
              { title: 'Professional Certification', body: 'Industry Bodies (IEEE, ACM, AMA)', courses: 12, status: 'active', icon: CheckCircle2, color: 'text-green-600 bg-green-50' },
              { title: 'Corporate Training Credits', body: 'HR Certification Institute', courses: 4, status: 'pending', icon: Zap, color: 'text-amber-600 bg-amber-50' },
            ].map((pathway) => {
              const Icon = pathway.icon;
              return (
                <div key={pathway.title} className="bg-card border border-border rounded-2xl p-5">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${pathway.color}`}>
                      <Icon size={18} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-bold text-foreground">{pathway.title}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${pathway.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                          {pathway.status}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">{pathway.body}</p>
                      <p className="text-xs text-muted-foreground mt-1"><span className="font-semibold text-foreground">{pathway.courses}</span> eligible courses</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add Partner Modal */}
      <Modal open={addPartnerOpen} onClose={() => setAddPartnerOpen(false)} title="Add Institutional Partner" size="md">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 block">Institution Name</label>
              <input className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30" placeholder="e.g. Harvard University" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 block">Type</label>
              <select className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30">
                <option>University</option><option>School District</option><option>Company</option><option>Research Lab</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 block">Integration Method</label>
              <select className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30">
                <option>Direct API</option><option>SCORM Export</option><option>LTI 1.3</option><option>LMS Bridge</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 block">Contact Email</label>
              <input type="email" className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30" placeholder="partnerships@institution.edu" />
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setAddPartnerOpen(false)} className="flex-1 py-2.5 border border-border rounded-xl text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors">Cancel</button>
            <button onClick={() => setAddPartnerOpen(false)} className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold btn-scale hover:bg-primary/90 transition-colors">
              Send Partnership Request
            </button>
          </div>
        </div>
      </Modal>

      {/* Endpoint Detail Modal */}
      <Modal open={endpointModalOpen} onClose={() => setEndpointModalOpen(false)} title="API Endpoint Details" size="md">
        {selectedEndpoint && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <span className={`text-sm font-bold px-3 py-1.5 rounded-lg border font-mono ${methodColor[selectedEndpoint.method]}`}>{selectedEndpoint.method}</span>
              <code className="text-sm font-mono text-foreground">{selectedEndpoint.path}</code>
            </div>
            <p className="text-sm text-muted-foreground">{selectedEndpoint.description}</p>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Example Request</p>
              <pre className="bg-muted rounded-xl p-4 text-xs font-mono text-foreground overflow-x-auto">{`curl -X ${selectedEndpoint.method} \\
  https://api.jarvondisu.edu/v1${selectedEndpoint.path} \\
  -H "Authorization: Bearer YOUR_API_KEY" \ -H"Content-Type: application/json"`}</pre>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${selectedEndpoint.status === 'stable' ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700'}`}>
                {selectedEndpoint.status}
              </span>
              <span className="text-xs text-muted-foreground">Rate limit: 1000 req/hour</span>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
